import type { ReactElement } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircleOutline,
  DoNotDisturb,
  ErrorOutline,
  HourglassTop,
  ReportProblem,
  Schedule,
  SkipNext,
  Verified,
  Warning,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ContactStatus } from '../types';

type StatusColor = 'default' | 'success' | 'warning' | 'error' | 'info';

interface ContactStatusChipProps {
  status?: ContactStatus;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const statusConfig: Record<ContactStatus, { labelKey: string; color: StatusColor; icon: ReactElement }> = {
  pending: { labelKey: 'components:statusChips.contact.labels.pending', color: 'default', icon: <Schedule sx={{ fontSize: 16 }} /> },
  in_progress: { labelKey: 'components:statusChips.contact.labels.in_progress', color: 'info', icon: <HourglassTop sx={{ fontSize: 16 }} /> },
  reached: { labelKey: 'components:statusChips.contact.labels.reached', color: 'success', icon: <CheckCircleOutline sx={{ fontSize: 16 }} /> },
  converted: { labelKey: 'components:statusChips.contact.labels.converted', color: 'success', icon: <Verified sx={{ fontSize: 16 }} /> },
  optout: { labelKey: 'components:statusChips.contact.labels.optout', color: 'warning', icon: <DoNotDisturb sx={{ fontSize: 16 }} /> },
  exhausted: { labelKey: 'components:statusChips.contact.labels.exhausted', color: 'error', icon: <ReportProblem sx={{ fontSize: 16 }} /> },
  mismatch: { labelKey: 'components:statusChips.contact.labels.mismatch', color: 'warning', icon: <Warning sx={{ fontSize: 16 }} /> },
  no_valid_phones: { labelKey: 'components:statusChips.contact.labels.no_valid_phones', color: 'error', icon: <ErrorOutline sx={{ fontSize: 16 }} /> },
  skipped: { labelKey: 'components:statusChips.contact.labels.skipped', color: 'default', icon: <SkipNext sx={{ fontSize: 16 }} /> },
};

function getPaletteColor(theme: Theme, color: StatusColor) {
  if (color === 'default') {
    return {
      bg: theme.palette.action.selected,
      text: theme.palette.text.secondary,
    };
  }
  return {
    bg: theme.palette[color].light,
    text: theme.palette[color].contrastText,
  };
}

export default function ContactStatusChip({ status, size = 'small', sx }: ContactStatusChipProps) {
  const theme = useTheme();
  const { t } = useTranslation(['components']);

  if (!status) return null;
  const config = statusConfig[status];
  const description = t(`components:statusChips.contact.descriptions.${status}`);
  const palette = getPaletteColor(theme, config.color);

  return (
    <Tooltip title={description} arrow>
      <Box component="span">
        <Chip
          icon={config.icon}
          label={t(config.labelKey, status)}
          size={size}
          sx={{
            bgcolor: palette.bg,
            color: palette.text,
            fontWeight: 600,
            textTransform: 'none',
            '& .MuiChip-icon': { fill: palette.text },
            ...sx,
          }}
        />
      </Box>
    </Tooltip>
  );
}
